import { DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TsButtonModule, TSIconModule, TsInputComponent } from 'tsui';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { AuthService } from 'app/core/services/auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TsSelectModule } from 'tsui/lib/ts-select/ts.select.module';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';
import { WorkspaceType, WORKSPACE_TYPE_OPTIONS } from '@core/models';

@Component({
  selector: 'create-workspace',
  standalone: true,
  imports: [
    TsDialogComponent,
    TsButtonModule,
    FormsModule,
    ReactiveFormsModule,
    TsInputComponent,
    TSIconModule,
    TsSelectModule,
  ],
  templateUrl: './create-workspace.component.html',
  styleUrls: ['./create-workspace.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateWorkspaceComponent implements OnInit {
  protected dialogRef = inject(DialogRef);
  protected destroyRef = inject(DestroyRef);
  private formBuilder = inject(FormBuilder);
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private store = inject(Store);

  form = this.formBuilder.group({
    workspace: this.formBuilder.control<string>('', { validators: [Validators.required] }),
    subdomain: this.formBuilder.control<string>('', { validators: [Validators.required] }),
    workspace_type: this.formBuilder.control<WorkspaceType>('poc'),
  });

  subdomainStatus: 'idle' | 'checking' | 'available' | 'taken' = 'idle';
  submitting = false;
  workspaceTypeOptions = WORKSPACE_TYPE_OPTIONS;

  ngOnInit(): void {}

  get isCreateDisabled(): boolean {
    return this.form.invalid || this.subdomainStatus !== 'available' || this.submitting;
  }

  termsheetUser(): boolean {
    return this.store.selectSnapshot(UserState.isTermSheetUser);
  }

  onSubdomainBlur(): void {
    const value = this.form.get('subdomain')?.value?.trim();
    if (!value) {
      this.subdomainStatus = 'idle';
      this.cdr.markForCheck();
      return;
    }
    this.subdomainStatus = 'checking';
    this.cdr.markForCheck();
    this.authService
      .validateWorkspace(value, 'subdomain')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (available) => {
          this.subdomainStatus = available ? 'available' : 'taken';
          this.cdr.markForCheck();
        },
        () => {
          this.subdomainStatus = 'taken';
          this.cdr.markForCheck();
        },
      );
  }

  create(): void {
    if (this.isCreateDisabled) return;

    this.submitting = true;
    this.cdr.markForCheck();
    const payload: any = {
      workspace_data: {
        workspace: this.form.get('workspace')?.value,
        subdomain: this.form.get('subdomain')?.value,
      },
    };

    if (this.termsheetUser()) {
      payload.workspace_data.workspace_type = this.form.get('workspace_type')?.value;
    }

    this.authService
      .createWorkspace(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response) => {
          this.submitting = false;
          if (response) {
            this.dialogRef.close(true);
            this.router.navigate(['/accounts']);
          } else {
            this.cdr.markForCheck();
          }
        },
        () => {
          this.submitting = false;
          this.cdr.markForCheck();
        },
      );
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
